/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef,useState } from 'react';

import { useSelector } from 'react-redux';

import { Select, Spin, Tooltip } from 'antd';

import { SCHEDULE_TYPES } from '@src/constants/commons';
import { ALLOWED_ROLES, getDivsWithAccess, resolveOrgAccess, useHasOrgWideAccess } from '@src/packages/utils';
import { resolveElasticScopeDivisionIds } from '@src/packages/utils/divisions/divisionIdsUtils';
import ScheduleService from '@src/services/ScheduleService';
import { sendErrorToSentry } from '@src/util/sentry/utils';

const roles = ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.SCHEDULES.LIST;
const { Option } = Select;
const ALL_SCHS = 'all';
const PAGE_SIZE = 50;
const SCROLL_THRESHOLD = 10;
const SEARCH_DEBOUNCE_MS = 300;
const MAX_NAME_LENGTH = 100;

const arrayToById = (array, idKey = 'id') => 
  array.reduce((acc, item) => ({ ...acc, [item[idKey]]: item }), {});

/**
 * Hook for managing schedule selection with search, pagination, and filtering capabilities.
 * 
 * @description
 * This hook provides comprehensive functionality for selecting schedules from Elasticsearch,
 * including infinite scroll pagination, debounced search, division-based filtering, and
 * selection state management. It automatically fetches schedules based on user permissions
 * and selected divisions.
 * 
 * @features
 * - ✅ Infinite scroll pagination with automatic loading
 * - ✅ Debounced search (300ms delay)
 * - ✅ Division-based access control and filtering
 * - ✅ Preserves selections during search/filter operations
 * - ✅ Support for initial schedule IDs (pre-selection)
 * - ✅ Optimized re-renders with memoization
 * - ✅ Error handling with Sentry integration
 * 
 * @param {Object} params - Hook parameters
 * @param {Object} params.selectedSchedules - Current selection state
 * @param {Object} params.selectedSchedules.byId - Map of selected schedules by ID
 * @param {boolean} params.selectedSchedules.selectedAll - Whether all schedules are selected
 * @param {Function} params.setSelected - Callback to update selection state
 * @param {Function} params.setLoading - Callback to update loading state
 * @param {string} [params.type='schedule'] - Schedule type filter (schedule|task|checkpoint_route)
 * @param {string[]} [params.divisionIds] - Query scope. Omit to use the divisions selector.
 *   `[]` is the resolved org-wide scope, not the omitted default — do not default this prop to `[]`.
 * @param {string[]} [params.initialScheduleIds=[]] - Optional array of schedule IDs to pre-select
 * 
 * @returns {Object} Hook return value
 * @returns {Array} schedules - Array of schedule objects currently loaded
 * @returns {boolean} loadingSchedules - Whether initial fetch is in progress
 * @returns {boolean} loadingMore - Whether pagination fetch is in progress
 * @returns {number} from - Current pagination offset
 * @returns {boolean} hasMore - Whether more schedules are available
 * @returns {Function} fetchSchedules - Manual fetch function(reset, searchTerm, idsToPreselect)
 * @returns {Function} loadMoreSchedules - Load next page of schedules
 * @returns {Function} handlePopupScroll - Scroll event handler for infinite scroll
 * @returns {Function} handleSearch - Debounced search handler(searchTerm)
 * @returns {Function} handleClear - Clear search and selection
 * @returns {Function} onChange - Selection change handler(values)
 * @returns {Array<React.Element>} options - Ant Design Select options with loading indicator
 * 
 * @example
 * ```javascript
 * const {
 *   schedules,
 *   loadingSchedules,
 *   handleSearch,
 *   onChange,
 *   options
 * } = useScheduleSelector({
 *   selectedSchedules: { byId: {}, selectedAll: false },
 *   setSelected: (selection) => console.log(selection),
 *   setLoading: (loading) => console.log(loading),
 *   type: SCHEDULE_TYPES.schedule,
 *   divisionIds: ['div-1', 'div-2'],
 *   initialScheduleIds: ['sch-1', 'sch-2']
 * });
 * ```
 * 
 * @important
 * - Selections are preserved during search operations (schedules not in search results remain selected)
 * - Division changes automatically reset selection to "select all"
 * - Initial schedule IDs are consumed only once on first render
 * - The hook respects user access permissions via ALLOWED_ROLES
 */
export const useScheduleSelector = ({
  selectedSchedules,
  setSelected,
  setLoading,
  type = SCHEDULE_TYPES.schedule,
  divisionIds,
  initialScheduleIds = [],
  exactMatch = false,
}) => {
  const scheduleService = useMemo(() => new ScheduleService(), []);
  
  const orgId = useSelector(state => state.organizations.organization.id);
  const rawSelectedDivsIds = useSelector(({ divisions }) => divisions.selector.ids || []);
  const claims = useSelector(({ user }) => user.access.data?.claims);
  const orgUserAccess = useSelector(({ user }) => user.orgUser?.data?.access);
  const userOrgAccess = resolveOrgAccess(claims, orgId, orgUserAccess);
  const userOrgDivisions = useSelector(({ divisions }) => divisions.assigned.data.divisions);
  
  const selectedDivsIds = useMemo(
    () => rawSelectedDivsIds || [],
    [JSON.stringify(rawSelectedDivsIds)],
  );

  const allowedDivs = useMemo(
    () => getDivsWithAccess(roles, userOrgAccess, userOrgDivisions) || [],
    [userOrgAccess, userOrgDivisions],
  );
  
  const allowedDivsIds = useMemo(() => allowedDivs.map(d => d.id), [allowedDivs]);
  
  const allowedSelectedDivs = useMemo(
    () => selectedDivsIds.filter(divId => allowedDivsIds.includes(divId)),
    [selectedDivsIds, allowedDivsIds]
  );
  
  /**
   * A consumer that passes `divisionIds` owns the query scope, so only an absent prop falls
   * back to the divisions selector.
   */
  const hasQueryScope = Array.isArray(divisionIds);

  const orgWide = useHasOrgWideAccess();

  const divsIdsToUse = useMemo(() => {
    if (!hasQueryScope) return allowedSelectedDivs;
    if (divisionIds.length > 0) return divisionIds;

    /**
     * `[]` means "everything I can reach". The generic-query API reads it as the whole
     * organization, which is only what an org-wide role may ask for: a division-scoped
     * user gets the query rejected, or worse, the division filter skipped. Resolving it
     * here keeps callers that pass an unset form value (`[]`) inside their own scope.
     */
    return resolveElasticScopeDivisionIds(allowedDivsIds, orgWide);
  }, [hasQueryScope, divisionIds, allowedSelectedDivs, allowedDivsIds, orgWide]);

  /* A division-scoped user with no assigned divisions resolves to `[]`, which would query
   * the whole organization — skip the request instead. */
  const canQuery = !!orgId && (divsIdsToUse.length > 0 || (hasQueryScope && orgWide));

  const [schedules, setSchedules] = useState([]);
  const schedulesRef = useRef([]);
  const hasConsumedInitialIds = useRef(false);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [from, setFrom] = useState(0);
  const fromRef = useRef(0);
  const [hasMore, setHasMore] = useState(true);
  const hasMoreRef = useRef(true);
  const debounceRef = useRef(null);
  const isMountedRef = useRef(true);
  const isInitializedRef = useRef(false);
  const loadingSchedulesRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const prevDepsRef = useRef({ orgId: null, divsIdsToUse: [], type: null });
  const isSearchActiveRef = useRef(false);
  const setSelectedRef = useRef(setSelected);
  setSelectedRef.current = setSelected;
  
  const schedulesById = useMemo(() => arrayToById(schedules), [schedules]);
  
  // Keep refs in sync with state
  useEffect(() => {
    schedulesRef.current = schedules;
  }, [schedules]);
  
  useEffect(() => {
    fromRef.current = from;
  }, [from]);
  
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  
  useEffect(() => {
    loadingSchedulesRef.current = loadingSchedules;
  }, [loadingSchedules]);
  
  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  const fetchSchedules = useCallback(
    async (reset = true, searchTerm = '', idsToPreselect = []) => {
      if (!canQuery) {
        if (reset) {
          setSchedules([]);
          setHasMore(false);
        }
        return;
      }

      if (!reset && (!hasMoreRef.current || loadingMoreRef.current || loadingSchedulesRef.current)) return;

      reset ? setLoadingSchedules(true) : setLoadingMore(true);

      try {
        const currentFrom = reset ? 0 : fromRef.current;
        const shouldUseInitialIds = reset && idsToPreselect?.length > 0;
        const idsToSearch = shouldUseInitialIds ? idsToPreselect : undefined;

        const result = await scheduleService.search({
          orgId,
          divsIds: divsIdsToUse,
          type,
          pageSize: PAGE_SIZE,
          from: currentFrom,
          searchTerm,
          ids: idsToSearch,
          exactMatch,
        });

        if (!isMountedRef.current) return;

        const hits = result?.hits?.hits || [];
        const newSchedules = hits.map(hit => ({ ...hit._source, id: hit._id }));
        const total = result?.hits?.total?.value || 0;
        const newFrom = currentFrom + newSchedules.length;

        // Combine schedules and remove duplicates by ID
        let updatedSchedules;
        if (reset) {
          updatedSchedules = newSchedules;
        } else {
          const combinedSchedules = [...schedulesRef.current, ...newSchedules];
          const uniqueSchedulesMap = new Map();
          combinedSchedules.forEach(sch => {
            if (!uniqueSchedulesMap.has(sch.id)) {
              uniqueSchedulesMap.set(sch.id, sch);
            }
          });
          updatedSchedules = Array.from(uniqueSchedulesMap.values());
        }
        
        setSchedules(updatedSchedules);
        setFrom(newFrom);
        setHasMore(newFrom < total);

        if (shouldUseInitialIds && idsToPreselect.length > 0) {
          const newSchedulesById = arrayToById(updatedSchedules);
          const selectedById = {};
          
          idsToPreselect.forEach(id => {
            if (newSchedulesById[id]) {
              selectedById[id] = newSchedulesById[id];
            }
          });

          if (Object.keys(selectedById).length > 0) {
            setSelected({ byId: selectedById, selectedAll: false });
          }
          
          hasConsumedInitialIds.current = true;
        }
      } catch (error) {
        sendErrorToSentry(error, { functionName: 'fetchSchedules' });
        console.error(`Error ${reset ? 'fetching' : 'loading more'} schedules:`, error);
        if (reset) setSchedules([]);
        setHasMore(false);
      } finally {
        reset ? setLoadingSchedules(false) : setLoadingMore(false);
      }
    },
    [orgId, divsIdsToUse, canQuery, type, setSelected],
  );

  const loadMoreSchedules = useCallback(() => {
    fetchSchedules(false);
  }, [fetchSchedules]);

  const handlePopupScroll = useCallback(
    e => {
      const { target } = e;
      if (target.scrollTop + target.offsetHeight >= target.scrollHeight - SCROLL_THRESHOLD) {
        loadMoreSchedules();
      }
    },
    [loadMoreSchedules]
  );

  const handleSearch = useCallback(
    value => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        isSearchActiveRef.current = !!value; // Mark search as active if there's a search term
        fetchSchedules(true, value);
      }, SEARCH_DEBOUNCE_MS);
    },
    [fetchSchedules],
  );

  const handleClear = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    isSearchActiveRef.current = false; // Clear search flag
    setSelected({ byId: {}, selectedAll: true });
    fetchSchedules(true, '');
  }, [fetchSchedules, setSelected]);

  const onChange = useCallback(
    values => {
      // Handle "select all" cases
      if (values.length > 0) {
        const lastValue = values[values.length - 1];
        const userSelectedAll = lastValue === ALL_SCHS;
        
        // User selected all individually only if:
        // 1. All visible schedules are selected
        // 2. First value is not ALL_SCHS
        // 3. There are no more schedules to load AND no active search
        const userSelectedAllIndividually = 
          values.length === schedules.length && 
          values[0] !== ALL_SCHS && 
          !hasMore &&
          !isSearchActiveRef.current;

        if (userSelectedAll || userSelectedAllIndividually) {
          if (debounceRef.current) {
            clearTimeout(debounceRef.current);
          }
          isSearchActiveRef.current = false; // Clear search flag when selecting all
          setSelected({ byId: {}, selectedAll: true });
          fetchSchedules(true, '');
          return;
        }
      }

      // Determine if we should preserve non-visible selections
      // If selectedAll was true, user is now making an explicit selection, don't preserve
      // If selectedAll was false, preserve non-visible selections (multi-select behavior)
      const shouldPreserveNonVisible = !selectedSchedules.selectedAll;

      const selectedById = {};
      
      // Process all values (includes visible and non-visible items from Select's value prop)
      values.forEach(schId => {
        if (schId === ALL_SCHS) return;
        
        // If the schedule is in the visible list, use the fresh data
        if (schedulesById[schId]) {
          selectedById[schId] = schedulesById[schId];
        } 
        // If not visible but was previously selected and we should preserve, keep it
        else if (shouldPreserveNonVisible && selectedSchedules.byId[schId]) {
          selectedById[schId] = selectedSchedules.byId[schId];
        }
      });
      
      setSelected({ byId: selectedById, selectedAll: false });
    },
    [schedules.length, schedulesById, selectedSchedules.byId, selectedSchedules.selectedAll, hasMore, setSelected, fetchSchedules]
  );

  // Set loading state
  useEffect(() => {
    setLoading(loadingSchedules);
  }, [loadingSchedules, setLoading]);

  // Reset selection when divisions change (only after initialization)
  useEffect(() => {
    if (isInitializedRef.current) {
      setSelectedRef.current({ byId: {}, selectedAll: true });
    }
  }, [selectedDivsIds]);

  // Auto-select all schedules on initial load (if no initial IDs provided) and,
  // while "all" is selected, absorb schedules created after the stored selection
  // so consumers can still resolve them by id.
  useEffect(() => {
    const hasInitialIds = initialScheduleIds && initialScheduleIds.length > 0;

    if (loadingSchedules || hasInitialIds || !schedules.length || !isInitializedRef.current) return;

    const hasNoSelection = !Object.keys(selectedSchedules.byId).length;

    if (hasNoSelection) {
      setSelectedRef.current({ byId: schedulesById, selectedAll: true });
      return;
    }

    if (!selectedSchedules.selectedAll) return;

    const hasUntrackedSchedules = Object.keys(schedulesById).some(
      id => !selectedSchedules.byId[id],
    );

    if (!hasUntrackedSchedules) return;

    setSelectedRef.current({
      byId: { ...selectedSchedules.byId, ...schedulesById },
      selectedAll: true,
    });
  }, [
    loadingSchedules,
    schedules.length,
    initialScheduleIds,
    selectedSchedules.byId,
    selectedSchedules.selectedAll,
    schedulesById,
  ]);

  // Main initialization effect - handles fetching schedules
  useEffect(() => {
    if (!canQuery) return;
    
    const prevDeps = prevDepsRef.current;
    const divsIdsString = JSON.stringify(divsIdsToUse);
    const prevDivsIdsString = JSON.stringify(prevDeps.divsIdsToUse);
    
    const depsChanged = 
      prevDeps.orgId !== orgId ||
      prevDivsIdsString !== divsIdsString ||
      prevDeps.type !== type;
    
    const shouldFetchWithInitialIds = initialScheduleIds?.length > 0 && !hasConsumedInitialIds.current;
    
    if (shouldFetchWithInitialIds) {
      fetchSchedules(true, '', initialScheduleIds);
      isInitializedRef.current = true;
    } else if (!isInitializedRef.current) {
      fetchSchedules(true, '');
      isInitializedRef.current = true;
    } else if (depsChanged) {
      fetchSchedules(true, '');
    }
    
    prevDepsRef.current = { orgId, divsIdsToUse, type };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, divsIdsToUse, canQuery, type, initialScheduleIds]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const options = useMemo(() => {
    const scheduleOptions = schedules.map(sch => (
      <Option key={sch.id} value={sch.id}>
        {sch.name.length > MAX_NAME_LENGTH ? (
          <Tooltip title={sch.name}>
            {sch.name.substring(0, MAX_NAME_LENGTH)} ...
          </Tooltip>
        ) : (
          sch.name
        )}
      </Option>
    ));

    if (loadingMore) {
      scheduleOptions.push(
        <Option key="loading" disabled>
          <div style={{ textAlign: 'center', padding: '8px' }}>
            <Spin size="small" />
          </div>
        </Option>,
      );
    }

    return scheduleOptions;
  }, [schedules, loadingMore]);

  return {
    schedules,
    loadingSchedules,
    loadingMore,
    from,
    hasMore,
    fetchSchedules,
    loadMoreSchedules,
    handlePopupScroll,
    handleSearch,
    handleClear,
    onChange,
    options,
  };
};
